import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  skipToken,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { api } from '@/api';
import { GetDeviceTrackingListParams } from '@/api/apis/route';
import { deviceTrackingQueryKeys } from './device-tracking.keys';
import { seedDeviceTrackingFromList } from '@/queries/device-tracking/utils';
import { PaginationSearchParams } from '@/api/types';
import { DEFAULT_INFINITE_QUERY_OPTIONS } from '@/queries/utils';

export const deviceTrackingQueries = {
  list: (params: GetDeviceTrackingListParams = {}, enabled?: boolean) =>
    queryOptions({
      queryKey: deviceTrackingQueryKeys.getList(params),
      queryFn: () => seedDeviceTrackingFromList(params),
      enabled,
    }),
  byId: (deviceTrackingId?: string) =>
    queryOptions({
      queryKey: deviceTrackingQueryKeys.getItem(deviceTrackingId),
      queryFn: deviceTrackingId ? () => api.route.getDeviceTrackingById(deviceTrackingId) : skipToken,
      enabled: Boolean(deviceTrackingId),
    }),
  byCaseId: (caseId?: string, enabled?: boolean) =>
    queryOptions({
      queryKey: deviceTrackingQueryKeys.getByCaseId(caseId),
      queryFn: caseId ? () => api.route.getDeviceTrackingByCaseId(caseId) : skipToken,
      enabled: enabled && Boolean(caseId),
    }),
  getInfiniteLogsById: ({ deviceTrackingId, ...filters }: PaginationSearchParams & { deviceTrackingId?: string }) =>
    infiniteQueryOptions({
      queryKey: deviceTrackingQueryKeys.getLogsById(filters, deviceTrackingId),
      queryFn: deviceTrackingId
        ? ({ pageParam: pageIndex }) => api.route.getDeviceTrackingLogs(deviceTrackingId, { ...filters, pageIndex })
        : skipToken,
      placeholderData: keepPreviousData,
      ...DEFAULT_INFINITE_QUERY_OPTIONS,
    }),
};

export const useDeviceTrackingListQuery = (params: GetDeviceTrackingListParams = {}, enabled = true) =>
  useQuery(deviceTrackingQueries.list(params, enabled));

export const useDeviceTrackingByIdQuery = (deviceTrackingId?: string) =>
  useQuery(deviceTrackingQueries.byId(deviceTrackingId));

export const useDeviceTrackingByCaseIdQuery = (caseId?: string, enabled = true) =>
  useQuery(deviceTrackingQueries.byCaseId(caseId, enabled));

export const useDeviceTrackingLogsQuery = ({
  searchQuery,
  ...params
}: PaginationSearchParams & { searchQuery?: string }) => {
  return useInfiniteQuery(
    deviceTrackingQueries.getInfiniteLogsById({
      itemsPerPage: 20,
      pageIndex: 0,
      deviceTrackingId: searchQuery,
      ...params,
    }),
  );
};
